import React, { useState, useEffect } from "react";
import "./PrintReport.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

const PrintReport = () => {
  useEscapeToGoBack();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [expiryDate, setExpiryDate] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date());
      const expiry = new Date();
      expiry.setMonth(expiry.getMonth() + 6);
      expiry.setDate(expiry.getDate() - 1);
      setExpiryDate(expiry.toLocaleDateString("en-GB"));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // --- helpers (unchanged) ---
  function getSingaporeDateNow() {
    const now = new Date();
    const sgOffset = 8 * 60;
    const localOffset = now.getTimezoneOffset();
    const diffInMs = (sgOffset + localOffset) * 60 * 1000;
    return new Date(now.getTime() + diffInMs);
  }
  const getStoredDateIso = async () => {
    try {
      const res = await axios.get("/api/system-date");
      return res?.data?.date || null;
    } catch {
      return null;
    }
  };
  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 0);
  };

  // --- handlers (unchanged) ---
  const handleDailyGrant = async () => {
    try {
      const response = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/daily-grant-pdf",
        { responseType: "blob" }
      );
      downloadBlob(response.data, "daily_grant.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handleDailyRedemption = async () => {
    try {
      const response = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/daily-redemption-summary-pdf",
        { responseType: "blob" }
      );
      const dateStr = new Date().toISOString().split("T")[0];
      downloadBlob(response.data, `daily_redemption_${dateStr}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handleDailyRedemptionSummary = async () => {
    try {
      const response = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/monthly-summary-pdf",
        { responseType: "blob" }
      );
      const dateStr = new Date().toISOString().split("T")[0];
      downloadBlob(response.data, `daily_redemption_summary_${dateStr}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handleDailyRecord = async () => {
    try {
      const response = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/daily-record-pdf",
        { responseType: "blob" }
      );
      downloadBlob(response.data, "daily_record.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handleDailyCashFlow = async () => {
    try {
      const formattedDate = getSingaporeDateNow().toISOString().split("T")[0];
      const response = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/daily-cashflow",
        { params: { date: formattedDate }, responseType: "blob" }
      );
      downloadBlob(response.data, "daily_cashflow.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handleDailyEndDay = async () => {
    try {
      const formattedDate = currentDate.toISOString().split("T")[0];
      const response = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/day-end-report",
        { params: { date: formattedDate }, responseType: "blob" }
      );
      downloadBlob(response.data, "end_day_report.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };
   const handleStockSummary9MRedirect = () => {
   navigate("/stock-summary-9m");
 };

  const handleAllDailyReports = async () => {
    try {
      const [grantRes, redemptionRes, recordRes] = await Promise.all([
        axios.get(
          "https://www.lianfoong.com/api/pawn-transactions/daily-grant-pdf",
          { responseType: "blob" }
        ),
        axios.get(
          "https://www.lianfoong.com/api/pawn-transactions/daily-redemption-pdf",
          { responseType: "blob" }
        ),
        axios.get(
          "https://www.lianfoong.com/api/pawn-transactions/daily-record-pdf",
          { responseType: "blob" }
        ),
      ]);
      const zip = new JSZip();
      zip.file("daily_grant.pdf", grantRes.data);
      zip.file("daily_redemption.pdf", redemptionRes.data);
      zip.file("daily_record.pdf", recordRes.data);
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "all_daily_reports.zip");
    } catch (error) {
      console.error("Error generating reports:", error);
    }
  };

  const handleExpiredUnredeemed = async () => {
    try {
      const response = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/expired-unredeemed-pdf",
        { responseType: "blob" }
      );
      downloadBlob(response.data, "expired_unredeemed.pdf");
    } catch (error) {
      console.error("Error generating expired/unredeemed report:", error);
    }
  };

  const handleStockSummary = async () => {
    try {
      const formattedDate = currentDate.toISOString().split("T")[0];
      const response = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/stock-summary-report",
        { params: { date: formattedDate }, responseType: "blob" }
      );
      downloadBlob(response.data, "stock_summary.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handleStockSummaryRedirect = () => {
    navigate("/stock-summary");
  };

  const handleForfeitedMmyyRedirect = () => {
   navigate("/forfeited-mmyy");
 };

  const handleSelectedGrant = async () => {
    try {
      const stored = await getStoredDateIso();
      const response = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/daily-grant-pdf/override",
        { responseType: "blob" }
      );
      const name = stored
        ? `daily_grant_${stored}.pdf`
        : "daily_grant_selected.pdf";
      downloadBlob(response.data, name);
    } catch (error) {
      if (error?.response?.status === 400) {
        alert("No stored date set. Please set it on the Change System Date page first.");
      } else {
        console.error("Error generating selected-date Daily Grant PDF:", error);
      }
    }
  };

  const handleSelectedCashflow = async () => {
    try {
      const stored = await getStoredDateIso();
      const response = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/daily-cashflow/override",
        { responseType: "blob" }
      );
      const name = stored
        ? `daily_cashflow_${stored}.pdf`
        : "daily_cashflow_selected.pdf";
      downloadBlob(response.data, name);
    } catch (error) {
      if (error?.response?.status === 400) {
        alert("No stored date set. Please set it on the Change System Date page first.");
      } else {
        console.error("Error generating selected-date Daily Cashflow PDF:", error);
      }
    }
  };

  const handleSelectedRedemptions = async () => {
    try {
      const stored = await getStoredDateIso();
      const response = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/daily-redemption-summary-pdf/override",
        { responseType: "blob" }
      );
      const name = stored
        ? `daily_redemption_${stored}.pdf`
        : "daily_redemption_selected.pdf";
      downloadBlob(response.data, name);
    } catch (error) {
      if (error?.response?.status === 400) {
        alert("No stored date set. Please set it on the Change System Date page first.");
      } else {
        console.error("Error generating selected-date Daily Redemptions PDF:", error);
      }
    }
  };

  const handleSelectedRecord = async () => {
  try {
    const stored = await getStoredDateIso();
    const response = await axios.get(
      "https://www.lianfoong.com/api/pawn-transactions/daily-record-pdf/override",
      { responseType: "blob" }
    );
    const name = stored ? `daily_record_${stored}.pdf` : "daily_record_selected.pdf";
    downloadBlob(response.data, name);
  } catch (error) {
    if (error?.response?.status === 400) {
      alert("No stored date set. Please set it on the Change System Date page first.");
    } else {
      console.error("Error generating selected-date Daily Record PDF:", error);
    }
  }
};

const handleUnredeemed8Zip = async () => {
  try {
    const res = await axios.get(
      "https://www.lianfoong.com/api/audit/unredeemed-csv/latest8",
      { responseType: "blob" }
    );
    downloadBlob(res.data, "unredeemed-latest8.zip");
  } catch (error) {
    console.error("Error downloading latest 8 months ZIP:", error);
    alert("Failed to download the ZIP. Please try again.");
  }
};

const handleActiveVaultRedirect = () => {
  navigate("/active-vault");
};

  
return (
  <div className="printreport">
    {/* Sticky header */}
    <div className="printreport__toolbar">
      <button className="printreport__back" onClick={() => navigate("/dashboard")}>
        Back
      </button>
    </div>

    {/* Scrollable content */}
    <div className="printreport__content">
      {/* Daily group */}
      <section className="printreport__panel">
        <h2 className="printreport__panelTitle">Daily Reports</h2>
        <div className="printreport__grid">
          <button onClick={handleDailyGrant}>Daily Grant</button>
          <button onClick={handleDailyRedemption}>Daily Redemption</button>
          <button onClick={handleDailyRecord}>Daily Record</button>
          <button onClick={handleDailyRedemptionSummary}>Daily Redemption – Summary</button>
          <button onClick={handleDailyCashFlow}>Daily Cashflow</button>
          <button onClick={handleAllDailyReports}>All Daily Reports (ZIP)</button>
          <button onClick={handleUnredeemed8Zip}>Stock Audit(ZIP)</button>
           {/* 
  <button onClick={handleDailyEndDay}>End Day Report</button>
  <button onClick={handleExpiredUnredeemed}>Expired Tickets Report</button>
  */}
         {/* <button onClick={handleStockSummary}>Stock Summary (PDF)</button>*/}
          <button onClick={handleStockSummaryRedirect}>Monthly Return</button>
          <button onClick={handleActiveVaultRedirect}>Stock Take</button>
           <button onClick={handleStockSummary9MRedirect}>Stock Summary</button>
           <button onClick={handleForfeitedMmyyRedirect}>Forfeited Tickets</button>
        </div>
      </section>

      {/* Selected date group */}
      <section className="printreport__panel">
  <h2 className="printreport__panelTitle">Selected Date (Stored)</h2>
  <div className="printreport__grid">
    <button onClick={handleSelectedGrant}>Selected Date – Grant</button>
    <button onClick={handleSelectedCashflow}>Selected Date – Daily Cashflow</button>
    <button onClick={handleSelectedRedemptions}>Selected Date – Daily Redemptions</button>
    <button onClick={handleSelectedRecord}>Selected Date – Daily Record</button>
  </div>
</section>
    </div>
  </div>
);
};

export default PrintReport;
